import { Injectable, signal, computed } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaymentLinkService } from '../services/payment-link.service';
import {
  PaymentLink,
  PaymentLinkListResponse,
  PaymentLinkStatus
} from '../models/payment-link/payment-link.model';
import { BankAccountsStore } from './bank-accounts.store';

@Injectable({
  providedIn: 'root'
})
export class DebtsStore {
  loading = signal(false);
  loadingDescription = signal('');
  paymentLinks = signal<PaymentLink[]>([]);
  filteredLinks = signal<PaymentLink[]>([]);
  totalLinks = signal(0);
  currentLink = signal<PaymentLink | null>(null);

  // New signal to track pending debts specifically
  pendingDebts = signal<PaymentLink[]>([]);
  // Computed signal to check if there are any pending debts
  hasPendingDebts = computed(() => this.pendingDebts().length > 0);

  constructor(
    private paymentLinkService: PaymentLinkService,
    private bankAccountsStore: BankAccountsStore,
    private matSnackBar: MatSnackBar
  ) {}

  async loadPaymentLinks(filter?: { status?: string }) {
    this.loading.set(true);
    try {
      if (filter?.status == 'all') filter.status = null;

      const modifiedFilter = {
        ...filter,
        sendedToAccount: this.bankAccountsStore.selectedBankAccountId()
      };

      const currentBankAccount = this.bankAccountsStore.selectedBankAccountId();
      const response = await this.paymentLinkService.getPaymentLinks(
        currentBankAccount,
        modifiedFilter
      );
      if (response.data) {
        const listResponse = response.data as PaymentLinkListResponse;
        this.paymentLinks.set(listResponse.items);
        this.filteredLinks.set(listResponse.items);
        this.totalLinks.set(listResponse.total);

        // Also update the pending debts if we don't have a filter
        // This ensures we always have the latest data for pending debts counter
        if (!filter || !filter.status) {
          // Find all active payment links (pending debts)
          const pending = listResponse.items.filter(
            (item) => item.status === PaymentLinkStatus.ACTIVE
          );
          this.pendingDebts.set(pending);
        }

        return listResponse;
      } else if (response.error) {
        this.matSnackBar.open(response.error?.message || 'Error al cargar deudas', 'Cerrar', {
          duration: 3000,
          panelClass: 'mat-snack-error'
        });
      }
    } catch (error) {
      console.error('Error al cargar deudas:', error);
      this.matSnackBar.open('Error al cargar deudas', 'Cerrar', {
        duration: 3000,
        panelClass: 'mat-snack-error'
      });
    } finally {
      this.loading.set(false);
    }
    return null;
  }

  async filterLinks(searchTerm: string, statusFilter: string | null = null) {
    // Create filter object for API call
    const filter: { description?: string; status?: string } = {};

    if (searchTerm && searchTerm.trim() !== '') {
      filter.description = searchTerm.trim();
    }

    if (statusFilter) {
      filter.status = statusFilter;
    }

    // Use the loadPaymentLinks method with the filter
    // await this.loadPaymentLinks(filter);
  }

  // New method to only load pending debts
  async loadPendingDebts() {
    // Set a minimal loading indicator
    const wasLoading = this.loading();
    if (!wasLoading) {
      this.loading.set(true);
    }

    try {
      const currentBankAccount = this.bankAccountsStore.selectedBankAccountId();
      const filter = {
        status: PaymentLinkStatus.ACTIVE,
        sendedToAccount: currentBankAccount
      };
      const response = await this.paymentLinkService.getPaymentLinks(currentBankAccount, filter);
      if (response.data) {
        const listResponse = response.data as PaymentLinkListResponse;
        this.pendingDebts.set(listResponse.items);
        return listResponse.items.length > 0;
      }
    } catch (error) {
      console.error('Error al cargar deudas pendientes:', error);
    } finally {
      if (!wasLoading) {
        this.loading.set(false);
      }
    }
    return false;
  }

  /**
   * Clears all debts data from the store
   * Used when a user logs out to ensure no data remains in memory
   */
  clearStore() {
    this.loading.set(false);
    this.loadingDescription.set('');
    this.paymentLinks.set([]);
    this.filteredLinks.set([]);
    this.totalLinks.set(0);
    this.currentLink.set(null);
    this.pendingDebts.set([]);
  }
}
